
// import { auth } from "../firebase";
// import { signInAnonymously } from "firebase/auth";

// export async function ensureSignedIn() {
//     // If a user is already signed in (admin or previous anon), do nothing
//     if (!auth.currentUser) {
//         await signInAnonymously(auth);
//     }
// }



// src/lib/authSession.js
import {
    signInAnonymously,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    createUserWithEmailAndPassword,
    updateProfile,
    onAuthStateChanged,
} from "firebase/auth";
import { auth } from "../firebase";

/**
 * Keep your existing anonymous sign-in for Storage Rules or public flows.
 * If a user is already signed in (admin/regular/previous anon), do nothing.
 */
export async function ensureSignedIn() {
    if (!auth.currentUser) {
        await signInAnonymously(auth);
    }
}

/**
 * Create account with Email + Password, then return a concise user object.
 * Also sets a basic displayName from email prefix if none provided elsewhere.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{uid:string,email:string|null,displayName:string|null, isAnonymous:boolean}>}
 */
export async function signUpWithEmailPassword(email, password) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const user = cred.user;

    // If no display name, set to email prefix (best-effort)
    if (!user.displayName && email) {
        const name = String(email).split("@")[0] || "User";
        try {
            await updateProfile(user, { displayName: name });
        } catch (err) {
            // non-blocking
            console.warn("updateProfile failed (non-blocking):", err);
        }
    }

    return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || null,
        isAnonymous: !!user.isAnonymous,
    };
}

/**
 * Sign in with Google using popup; if popup is blocked, falls back to redirect.
 * After redirect, call this function again OR rely on page load to resolve to currentUser.
 *
 * @returns {Promise<{uid:string,email:string|null,displayName:string|null, isAnonymous:boolean}>}
 */
export async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();

    // Try popup first
    try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        return {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || null,
            isAnonymous: !!user.isAnonymous,
        };
    } catch (err) {
        // If popup blocked or requires redirect (e.g., in iOS PWA), try redirect
        if (isPopupBlockedOrUnavailable(err)) {
            await signInWithRedirect(auth, provider);
            // The page will redirect and resolve after reload; optionally support:
            const redirectResult = await getRedirectResult(auth).catch(() => null);
            if (redirectResult?.user) {
                const user = redirectResult.user;
                return {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || null,
                    isAnonymous: !!user.isAnonymous,
                };
            }
            // If we get here, the redirect flow will complete on page reload
            // Return a placeholder; your UI can also wait on onAuthStateChanged
            return {
                uid: "",
                email: null,
                displayName: null,
                isAnonymous: false,
            };
        }
        throw err;
    }
}

/**
 * Lightweight check for common popup errors.
 */
function isPopupBlockedOrUnavailable(error) {
    const code = error?.code || "";
    return (
        code === "auth/popup-blocked" ||
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/operation-not-supported-in-this-environment"
    );
}

/**
 * Returns current user snapshot (null if not signed in).
 * @returns {{uid:string,email:string|null,displayName:string|null,isAnonymous:boolean}|null}
 */
export function getCurrentUser() {
    const u = auth.currentUser;
    if (!u) return null;
    return {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName || null,
        isAnonymous: !!u.isAnonymous,
    };
}

/**
 * Optional: await a non-anonymous signed-in user.
 * Useful if you want to hard-require auth (not used in your public flow by default).
 *
 * @returns {Promise<{uid:string,email:string|null,displayName:string|null,isAnonymous:boolean}>}
 */
export function requireAuth() {
    return new Promise((resolve, reject) => {
        const unsub = onAuthStateChanged(
            auth,
            (u) => {
                if (u && !u.isAnonymous) {
                    resolve({
                        uid: u.uid,
                        email: u.email,
                        displayName: u.displayName || null,
                        isAnonymous: !!u.isAnonymous,
                    });
                } else {
                    reject(new Error("Authentication required."));
                }
                unsub();
            },
            (err) => {
                unsub();
                reject(err);
            }
        );
    });
}
